import type { Ability, Section } from "@qendo/permissions";
import type { Href } from "expo-router";
import { type ReactNode, useCallback } from "react";
import { View } from "react-native";
import { Can, type CanProps } from "~/components/permission/Can";
import { Dismiss } from "~/components/permission/Dismiss";

type WithCannot = {
    onCannot: () => void;
    dismissTo?: never;
    dismissAfter?: never;
    otherwise?: never;
};

type WithDismiss = {
    onCannot?: never;
    dismissTo: Href;
    dismissAfter?: number;
    otherwise?: never;
};

type WithoutCannot = {
    onCannot?: never;
    dismissTo?: never;
    dismissAfter?: never;
    otherwise?: ReactNode | (() => ReactNode);
};

type AuthorizeProps<S extends Section, A extends Ability<S>> = {
    onCannot?: () => void;
} & Omit<CanProps<S, A>, "not"> &
    (WithCannot | WithDismiss | WithoutCannot);

export const Authorize = <S extends Section = Section, A extends Ability<S> = Ability<S>>({
    onCannot,
    otherwise,
    dismissTo,
    dismissAfter,
    children,
    ...canProps
}: AuthorizeProps<S, A>) => {
    const on_otherwise = useCallback(
        () =>
            typeof otherwise === "function"
                ? otherwise()
                : (otherwise ??
                  (onCannot ? <View onLayout={onCannot} /> : <Dismiss to={dismissTo} after={dismissAfter} />)),
        [otherwise, onCannot, dismissTo, dismissAfter],
    );

    return (
        <Can {...canProps} otherwise={on_otherwise}>
            {children}
        </Can>
    );
};
